import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { projectsApi } from '@/api/projects.api';
import type { ProjectAnalytics } from '@/types/projects';
import { showApiError } from '@/utils/apiError';

const REPORTS = ['projects', 'tasks', 'issues', 'timesheets', 'risks', 'sprints'] as const;

export function ProjectsReportsPage() {
  const [stats, setStats] = useState<ProjectAnalytics | null>(null);

  useEffect(() => {
    projectsApi.analyticsDashboard().then(setStats).catch((e) => showApiError(e, 'Reports unavailable'));
  }, []);

  const download = async (reportType: string, format: string) => {
    try {
      const blob = await projectsApi.exportReport(reportType, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}.${format.toLowerCase() === 'excel' ? 'xlsx' : format.toLowerCase()}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showApiError(err, 'Export failed');
    }
  };

  return (
    <div>
      <PageHeader title="Project Reports" description="Delivery analytics and exportable reports"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Reports' }]} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="card p-4"><p className="text-sm text-slate-500">Projects</p><p className="text-2xl font-semibold">{stats?.totalProjects ?? '—'}</p></div>
        <div className="card p-4"><p className="text-sm text-slate-500">Tasks</p><p className="text-2xl font-semibold">{stats?.totalTasks ?? '—'}</p></div>
        <div className="card p-4"><p className="text-sm text-slate-500">Budget usage</p><p className="text-2xl font-semibold">{stats ? `${stats.budgetUsagePercent.toFixed(0)}%` : '—'}</p></div>
        <div className="card p-4"><p className="text-sm text-slate-500">Open risks</p><p className="text-2xl font-semibold">{stats?.openRisks ?? '—'}</p></div>
      </div>
      <div className="card p-4">
        <h3 className="font-semibold mb-4">Export reports</h3>
        <div className="space-y-3">
          {REPORTS.map((r) => (
            <div key={r} className="flex items-center justify-between border-b pb-2">
              <span className="capitalize">{r}</span>
              <div className="flex gap-2">
                {(['CSV', 'EXCEL', 'PDF'] as const).map((fmt) => (
                  <Button key={fmt} variant="secondary" onClick={() => void download(r, fmt)}>{fmt}</Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
